// Progressive rendering + request supersession for one dashboard's charts
// (design doc §6). Owns the whole render loop a dashboard page needs:
//
//   1. POST /query with every visible chart id -> paints whatever comes back
//      already "ready" immediately (a cache hit resolves inline, per
//      orchestration.py's `fan_out_chart_query` — a warm dashboard costs no
//      round trip past this one POST).
//   2. Subscribes ONCE (dashboard-wide, not per chart) to `channel_for_bi`
//      and MERGES every `BiChartsSettled` batch into the running per-chart
//      status map by `chart_id` (never replace — one event lists only what
//      newly settled since the last publish).
//   3. A filter change (or a dashboard/chart-set change) bumps a local
//      generation counter; any result that resolves under a superseded
//      generation is discarded, never painted — the guarantee that a reader
//      never sees a number computed under an old filter state labeled as
//      current.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { usePusherChannel } from "@bi/shared";
import type {
  BiChartResultResponse,
  BiChartsSettled,
  BiQueryResponse,
  ChartResult,
  FilterState,
} from "@bi/shared/types";

import { biChannel, biPusherConfig } from "../config";
import { errorMessage } from "../components/states";
import { createAcceptedRequests } from "./accepted-requests";
import { queryDashboardCharts } from "./api";
import { chartResultQuery } from "./queries";
import {
  loadResultKeyMap,
  lookupResultKey,
  rememberResultKeys,
  saveResultKeyMap,
  type RememberedKey,
  type ResultKeyMap,
} from "./result-key-map";
import { createSettleBuffer } from "./settle-buffer";

/** How often a still-pending chart re-reads its result over REST. Comfortably
 *  above a typical BigQuery answer, and the read itself is a cache/Postgres
 *  lookup rather than a query, so this costs the warehouse nothing. */
const POLL_MS = 2_500;

/** How long polling keeps trying before calling a chart timed out. Above the
 *  server's own 90s backstop on purpose: the server settles `timeout` over
 *  Pusher, so when that transport is the thing that is broken this deadline is
 *  the only one the reader will ever see. */
const POLL_DEADLINE_MS = 150_000;

/** Mark charts the poll deadline gave up on as timed out — and ONLY those.
 *
 *  Pulled out and exported because it is the one step here that can overwrite
 *  good state. `stranded` is read from `pendingKeysRef`, which a settle event
 *  may have emptied a moment earlier, so a chart in that list can already have
 *  landed; rewriting it would replace a rendered result with "took too long".
 *  The status check, not the list, is what decides.
 */
export function strandPending(
  charts: Readonly<Record<string, ChartRenderState>>,
  stranded: readonly string[],
): Record<string, ChartRenderState> {
  const next = { ...charts };
  for (const chartId of stranded) {
    if (next[chartId]?.status !== "loading") continue;
    next[chartId] = {
      status: "error",
      message: "This chart took too long to answer.",
      isTimeout: true,
    };
  }
  return next;
}

// The (dashboard, filters, chart) -> result_key memory, module scope because
// it is a property of this browser profile rather than of one mounted hook:
// two dashboards open in two tabs share it, and a remount must not re-read
// localStorage. See result-key-map.ts for why the association has to be
// remembered at all (short version: the persisted query cache keys results by
// `result_key`, which only the server knows, and waiting to be told it is the
// round trip we are painting through).
let rememberedKeys: ResultKeyMap | null = null;

function keyMemory(): ResultKeyMap {
  rememberedKeys ??= loadResultKeyMap();
  return rememberedKeys;
}

function recordResultKeys(entries: readonly RememberedKey[]): void {
  if (entries.length === 0) return;
  rememberedKeys = rememberResultKeys(keyMemory(), entries, { now: Date.now() });
  saveResultKeyMap(rememberedKeys);
}

export type ChartRenderState =
  | { status: "loading" }
  /** `refreshing: true` means a NEW request is in flight for this chart but
   *  `result` is still the last-known-good answer — design doc §5's
   *  "dimmed-stale, not blanked" rule: a chart that already has data never
   *  regresses to a bare skeleton on a filter change, only a visibly dimmed
   *  version of what it already had. `ChartCard` is what makes the dimming
   *  visible rather than disguised — never render `refreshing` silently. */
  | { status: "ready"; result: ChartResult; refreshing?: boolean }
  | { status: "error"; message: string; isTimeout: boolean };

/**
 * What differs between an AUTHORED dashboard and a BUILT one: only these three
 * things. The rest of this hook is shared because the server made both the
 * same (same `result_key`, same fan-out, same `bi.charts.settled` event).
 *
 * `fan_out_chart_query` publishes `dashboard_id = str(dashboard.uuid)`, the
 * SPEC's uuid, not the studio's row id. A wrong id is invisible on a warm
 * dashboard and hangs every tile on a cold one, so it is a parameter.
 */
export interface BiQuerySourceOptions {
  /** Issues the query. Defaults to the authored `POST /api/bi/dashboards/{id}/query`. */
  runQuery?: (chartIds: readonly string[], filters: FilterState) => Promise<BiQueryResponse>;
  /** The `dashboard_id` a `BiChartsSettled` event carries for this dashboard.
   *  Defaults to `dashboardId`, which is correct for the authored route because
   *  there the row id and the spec uuid are the same value. */
  eventDashboardId?: string;
  /** Bumped by the caller when the CHART SPECS changed rather than the filters
   *  — the canvas edits a chart in place, so its id and the filter state both
   *  stay put while what it means changes completely. Without it a preview
   *  would paint the first version of a tile and never update. */
  revision?: string;
}

export interface UseBiDashboardQueryResult {
  /** Every requested chart's current render state, keyed by chart id. A
   *  chart id not yet in this map hasn't been requested at all (the initial
   *  render, before the first `/query` POST resolves). */
  charts: Record<string, ChartRenderState>;
  /** Re-issues `/query` for exactly one chart (§7's per-chart retry
   *  affordance) — never gated on the shared generation counter, since a
   *  reader's explicit retry of THIS chart is always the latest intent for
   *  it regardless of what else is in flight for the rest of the dashboard. */
  retryChart: (chartId: string) => void;
}

/** One settle-shaped entry, normalized across the two sources this hook
 *  reads. The inline `/query` response (`BiChartStatusEntry`, a `WireModel`)
 *  is camelCase. The `BiChartsSettled` event's `BiChartStatus` (a plain
 *  `BaseModel`) is snake_case. Only the inline response can report
 *  "pending". */
interface SettleEntry {
  chartId: string;
  status: "ready" | "pending" | "error" | "timeout";
  resultKey: string;
  error?: string | null;
}

/** The Pusher event's snake_case charts, in this module's own convention.
 *  Module scope: it closes over nothing, and both the live path and the
 *  held-event replay must read an event exactly the same way. */
function entriesFromEvent(event: BiChartsSettled): SettleEntry[] {
  return event.charts.map((c) => ({
    chartId: c.chart_id,
    status: c.status,
    resultKey: c.result_key,
    error: c.error,
  }));
}

export function useBiDashboardQuery(
  dashboardId: string,
  chartIds: readonly string[],
  filters: FilterState,
  employeeEmail: string,
  options: BiQuerySourceOptions = {},
): UseBiDashboardQueryResult {
  const queryClient = useQueryClient();
  const [charts, setCharts] = useState<Record<string, ChartRenderState>>({});

  // Held in a ref, not read from `options` inside the callbacks: a caller that
  // builds its `runQuery` inline (every caller) hands a fresh function every
  // render, and depending on it would re-arm `runQuery`'s useCallback — and so
  // the query effect — on every keystroke. The ref keeps the LATEST function
  // while the callback identity stays keyed on the things that should actually
  // re-issue a request.
  const runQueryRef = useRef(options.runQuery);
  runQueryRef.current = options.runQuery;
  const eventDashboardId = options.eventDashboardId ?? dashboardId;
  const revision = options.revision ?? "";

  const issue = useCallback(
    (ids: readonly string[], currentFilters: FilterState): Promise<BiQueryResponse> => {
      const custom = runQueryRef.current;
      if (custom) return custom(ids, currentFilters);
      return queryDashboardCharts(dashboardId, {
        chartIds: [...ids],
        filters: currentFilters,
        forceRefresh: false,
      });
    },
    [dashboardId],
  );

  // Request supersession bookkeeping (design doc §6). `generationRef` is the
  // fast client-side guard for a result that resolves inline before any
  // server id is known; `acceptedRef` is the authoritative check
  // against a `BiChartsSettled` event's `turn_id` (== the server's
  // `BiQueryResponse.request_id` — `orchestration.py` publishes with
  // `turn_id=request_id`), which also protects against a stale event
  // surviving a full navigation.
  //
  // A SET, not a single id: `retryChart` issues its own `/query` and gets its
  // own `request_id` back while the dashboard-wide batch may still be in
  // flight. Holding one id would mean whichever request answered last silently
  // orphaned the other's events, leaving those charts loading forever. A new
  // generation (a filter change) clears the set, because it supersedes an
  // in-flight retry just as much as the batch that spawned it.
  const generationRef = useRef(0);
  const acceptedRef = useRef(createAcceptedRequests());

  // The filter serialization the in-flight request was issued under, so a
  // resolving entry can be filed in the cross-session key memory against the
  // right filter state. Captured at REQUEST time for the same reason
  // `retryChart` captures its generation there: by the time an entry lands,
  // `filters` may already be the reader's NEXT selection.
  const requestFiltersKeyRef = useRef("");

  // chartId -> result_key for every chart still waiting on a settle event.
  // Pusher replays NOTHING published while the socket was down, so this is
  // what `reconcilePending` re-reads over REST after a reconnect; without it
  // a settle that lands in the gap leaves the tile loading until a manual
  // refresh (usePusherChannel's own contract calls this out).
  const pendingKeysRef = useRef(new Map<string, string>());

  // Settle events that beat their own `/query` response — see
  // `settle-buffer.ts` for why dropping one is permanent rather than merely
  // late. Held, never trusted: a held event still only paints once a response
  // accepts its `turn_id`.
  const settleBufferRef = useRef(createSettleBuffer<BiChartsSettled>());

  const applyEntries = useCallback(
    (entries: readonly SettleEntry[], generation: number) => {
      // Every entry that names a result key teaches the key memory one
      // (dashboard, filters, chart) -> result_key association, including a
      // "pending" one: the key is known the moment the query compiles, and a
      // chart still computing is exactly the tile whose LAST result is worth
      // painting on the next open.
      recordResultKeys(
        entries
          .filter((entry) => entry.resultKey)
          .map((entry) => ({
            dashboardId,
            filtersKey: requestFiltersKeyRef.current,
            chartId: entry.chartId,
            resultKey: entry.resultKey,
          })),
      );
      for (const entry of entries) {
        if (entry.status === "pending") {
          pendingKeysRef.current.set(entry.chartId, entry.resultKey); // already painted as loading
          continue;
        }
        pendingKeysRef.current.delete(entry.chartId);
        if (entry.status === "ready") {
          queryClient
            .fetchQuery(chartResultQuery(entry.resultKey))
            .then((resp) => {
              if (generation !== generationRef.current) return; // superseded
              // `refreshing` clears here — this IS the fresh result landing.
              setCharts((prev) => ({
                ...prev,
                [entry.chartId]: { status: "ready", result: resp.result },
              }));
            })
            .catch((err: unknown) => {
              if (generation !== generationRef.current) return;
              setCharts((prev) => ({
                ...prev,
                [entry.chartId]: { status: "error", message: errorMessage(err), isTimeout: false },
              }));
            });
          continue;
        }
        // error / timeout
        if (generation !== generationRef.current) continue;
        setCharts((prev) => ({
          ...prev,
          [entry.chartId]: {
            status: "error",
            message:
              entry.error ??
              (entry.status === "timeout"
                ? "This chart took too long to answer."
                : "This chart failed to load."),
            isTimeout: entry.status === "timeout",
          },
        }));
      }
    },
    [queryClient, dashboardId],
  );

  // Called immediately after every `accept()`, both request paths. A response
  // arriving is the only thing that can turn a held event into an applicable
  // one, so this is the complete set of drain points.
  const releaseHeldSettles = useCallback(
    (generation: number) => {
      for (const event of settleBufferRef.current.release(generation, (turnId) =>
        acceptedRef.current.isAccepted(turnId),
      )) {
        applyEntries(entriesFromEvent(event), generation);
      }
    },
    [applyEntries],
  );

  /** The last result this chart resolved to under this exact filter state, if
   *  the persisted query cache still holds it (`cache-persist.ts` bounds that
   *  by the SAME 3 hours as the backend's `RESULT_TTL_SECONDS`). `undefined`
   *  is the ordinary cold-start answer and simply means a skeleton. */
  const persistedResult = useCallback(
    (chartId: string, filtersKey: string): ChartResult | undefined => {
      const resultKey = lookupResultKey(
        keyMemory(),
        { dashboardId, filtersKey, chartId },
        { now: Date.now() },
      );
      if (!resultKey) return undefined;
      const cached = queryClient.getQueryData<BiChartResultResponse>(
        chartResultQuery(resultKey).queryKey,
      );
      return cached?.result;
    },
    [dashboardId, queryClient],
  );

  const runQuery = useCallback(
    (ids: readonly string[], currentFilters: FilterState) => {
      if (ids.length === 0) return;
      const generation = ++generationRef.current;
      requestFiltersKeyRef.current = JSON.stringify(currentFilters);
      acceptedRef.current.reset();
      // A new generation supersedes every in-flight result key, exactly as it
      // supersedes the accepted-request set — a reconnect must not reconcile
      // a tile back to a number computed under the PREVIOUS filter state.
      pendingKeysRef.current.clear();
      // Same reasoning for the held events: a new generation supersedes them,
      // and nothing will ever accept their request ids again.
      settleBufferRef.current.clear();
      const filtersKey = requestFiltersKeyRef.current;
      setCharts((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          const existing = prev[id];
          // Dimmed-stale rule (design doc §5): a chart with data keeps its last
          // result, marked refreshing, instead of a skeleton. A result persisted from
          // an earlier session counts too (cache-persist.ts).
          const carried =
            existing?.status === "ready" ? existing.result : persistedResult(id, filtersKey);
          next[id] = carried
            ? { status: "ready", result: carried, refreshing: true }
            : { status: "loading" };
        }
        return next;
      });
      issue(ids, currentFilters)
        .then((resp) => {
          if (generation !== generationRef.current) return; // superseded
          acceptedRef.current.accept(resp.requestId);
          applyEntries(
            resp.charts.map((c) => ({
              chartId: c.chartId,
              status: c.status,
              resultKey: c.resultKey,
              error: c.error,
            })),
            generation,
          );
          // AFTER the response's own entries: a held event is the newer fact
          // (the chart settled after this response was built), so it must win.
          releaseHeldSettles(generation);
        })
        .catch((err: unknown) => {
          if (generation !== generationRef.current) return;
          setCharts((prev) => {
            const next = { ...prev };
            for (const id of ids) {
              next[id] = { status: "error", message: errorMessage(err), isTimeout: false };
            }
            return next;
          });
        });
    },
    [dashboardId, issue, applyEntries, releaseHeldSettles, persistedResult],
  );

  const chartIdsKey = useMemo(() => [...chartIds].sort().join(","), [chartIds]);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  // The effect's own deps are the STABLE derived keys, not `chartIds`/
  // `filters` themselves (fresh array/object identity every render would
  // otherwise re-query on every render, not just an actual change).
  useEffect(() => {
    runQuery(chartIds, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on chartIdsKey/filtersKey/revision by design, see above
  }, [dashboardId, chartIdsKey, filtersKey, revision]);

  const retryChart = useCallback(
    (chartId: string) => {
      // Captured at REQUEST time: once the generation moves on, this answer is
      // for filters the reader has left, and the filter change's own `/query`
      // covers this chart. Reading `generationRef.current` in `.then` would
      // `accept()` a superseded request id after `runQuery` reset the set, so its
      // later settles would render as current.
      const generation = generationRef.current;
      issue([chartId], filters)
        .then((resp) => {
          if (generation !== generationRef.current) return; // superseded
          // Accept this retry's own request id too. A retried chart that comes
          // back `pending` is finished over Pusher under THIS id, not the
          // batch's, so without it the settle event is discarded and the chart
          // stays loading forever with no timeout path in the UI.
          acceptedRef.current.accept(resp.requestId);
          applyEntries(
            resp.charts.map((c) => ({
              chartId: c.chartId,
              status: c.status,
              resultKey: c.resultKey,
              error: c.error,
            })),
            generation,
          );
          // A retry loses the same race — its chart is often the FAST one, so
          // a settle beating this response is the likely case, not the corner.
          releaseHeldSettles(generation);
        })
        .catch((err: unknown) => {
          if (generation !== generationRef.current) return; // superseded
          setCharts((prev) => ({
            ...prev,
            [chartId]: { status: "error", message: errorMessage(err), isTimeout: false },
          }));
        });
      // The same dimmed-stale rule as `runQuery` (design doc §5): retry is usually
      // pressed on a rendered tile, so keep its last result, marked refreshing.
      setCharts((prev) => {
        const existing = prev[chartId];
        return {
          ...prev,
          [chartId]:
            existing?.status === "ready"
              ? { status: "ready", result: existing.result, refreshing: true }
              : { status: "loading" },
        };
      });
    },
    [issue, filters, applyEntries, releaseHeldSettles],
  );

  // Re-read every still-pending chart over REST. Pusher never replays events
  // published while the socket was down, so a transport upgrade or an
  // ordinary blip during a 20s BigQuery scan can swallow a whole
  // `BiChartsSettled` batch — and nothing else would ever repaint those
  // tiles, because the 90s backstop publishes over the SAME dead socket. The
  // client already holds each chart's `result_key`, so the authoritative
  // state is one GET away. A 404 means "genuinely not computed yet", which is
  // the ordinary case and simply leaves the chart loading.
  const reconcilePending = useCallback(() => {
    const generation = generationRef.current;
    for (const [chartId, resultKey] of pendingKeysRef.current) {
      queryClient
        .fetchQuery(chartResultQuery(resultKey))
        .then((resp) => {
          if (generation !== generationRef.current) return; // superseded
          pendingKeysRef.current.delete(chartId);
          setCharts((prev) => ({ ...prev, [chartId]: { status: "ready", result: resp.result } }));
        })
        .catch(() => {
          // Not computed yet (404) or unreachable — stay loading and let the
          // next settle event or reconnect carry it.
        });
    }
  }, [queryClient]);

  // Polling makes Pusher an optimisation, not a dependency. `reconcilePending`
  // is the authoritative read. Without the poll, nothing drives it when the
  // socket is healthy but the server cannot publish (prod's worker once had no
  // PUSHER_* config, so cold dashboards hung forever).
  //
  // The interval exists only while something is pending, so a warm dashboard
  // makes no requests. Each read is a cache/Postgres lookup, never BigQuery.
  // The deadline ends a permanent spinner: an abandoned chart 404s forever, and
  // the server's 90s backstop settles over the same, possibly broken, Pusher.
  //
  // Depends on the BOOLEAN, not on `charts`: depending on the map rebuilds the
  // interval (and resets `startedAt`) each time a tile lands, so the deadline
  // would never be reached.
  const hasPendingCharts = Object.values(charts).some((s) => s.status === "loading");

  useEffect(() => {
    if (!hasPendingCharts) return;

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - startedAt < POLL_DEADLINE_MS) {
        reconcilePending();
        return;
      }
      window.clearInterval(timer);
      const stranded = [...pendingKeysRef.current.keys()];
      pendingKeysRef.current.clear();
      if (stranded.length === 0) return;
      setCharts((prev) => strandPending(prev, stranded));
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [hasPendingCharts, reconcilePending]);

  // Progressive rendering via Pusher (step 2 in the header).
  const channel = employeeEmail ? biChannel(employeeEmail) : null;
  const pusherConfig = useMemo(() => biPusherConfig(), []);
  const settledEvents = usePusherChannel<BiChartsSettled>(
    channel,
    ["bi.charts.settled"],
    pusherConfig,
    reconcilePending, // onReconnect: close the replay gap
    reconcilePending, // onSubscriptionError: same recovery, one REST read
  );
  const processedCount = useRef(0);

  useEffect(() => {
    const fresh = settledEvents.slice(processedCount.current);
    processedCount.current = settledEvents.length;
    const generation = generationRef.current;
    for (const event of fresh) {
      if (event.dashboard_id !== eventDashboardId) continue;
      // An unaccepted turn_id means one of two very different things, and
      // treating them alike is what stranded a tile: either this is a
      // SUPERSEDED request (discard), or it is the request we just fired and
      // whose id we do not know yet, because the settle beat the HTTP
      // response. Held rather than judged — `releaseHeldSettles` applies it
      // the moment a response accepts the id, and a genuinely superseded one
      // never matches and ages out with its generation.
      if (!acceptedRef.current.isAccepted(event.turn_id)) {
        settleBufferRef.current.hold(event, generation);
        continue;
      }
      applyEntries(entriesFromEvent(event), generation);
    }
  }, [settledEvents, eventDashboardId, applyEntries]);

  return { charts, retryChart };
}
